/**
 * Storage layer for recommendation caching
 *
 * Provides in-memory and localStorage caching with TTL support.
 * Gracefully degrades when localStorage is unavailable.
 */

import { RecommendationCache } from "./types";
import { STORAGE_KEYS } from "./types";

// In-memory cache singleton
let memoryCache: RecommendationCache | null = null;

/**
 * Check if localStorage is available and writable
 */
export function isStorageAvailable(): boolean {
  try {
    const testKey = `__recommendation_test_${Date.now()}`;
    localStorage.setItem(testKey, "test");
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if a cache entry is still valid based on TTL
 */
export function isCacheValid(cache: RecommendationCache | null): boolean {
  if (!cache) return false;

  const now = Date.now();
  const cacheAge = now - cache.timestamp;

  return cacheAge < cache.ttl;
}

/**
 * Get recommendation cache from memory or localStorage
 *
 * Priority:
 * 1. Valid in-memory cache
 * 2. Valid localStorage cache
 * 3. null (no valid cache)
 */
export function getCache(): RecommendationCache | null {
  // Check in-memory cache first
  if (memoryCache && isCacheValid(memoryCache)) {
    return memoryCache;
  }

  // Clear invalid memory cache
  if (memoryCache && !isCacheValid(memoryCache)) {
    memoryCache = null;
  }

  // Try localStorage if available
  if (isStorageAvailable()) {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CACHE);
      if (!data) {
        return null;
      }

      const cache = JSON.parse(data) as RecommendationCache;

      // Validate cache structure
      if (
        !cache ||
        typeof cache !== "object" ||
        !Array.isArray(cache.products) ||
        typeof cache.timestamp !== "number" ||
        typeof cache.ttl !== "number"
      ) {
        console.warn("Invalid recommendation cache structure in localStorage");
        clearCache();
        return null;
      }

      // Check TTL
      if (isCacheValid(cache)) {
        // Update memory cache
        memoryCache = cache;
        return cache;
      } else {
        // Cache expired, clean it up
        clearCache();
        return null;
      }
    } catch (error) {
      console.warn("Failed to read recommendation cache from localStorage:", error);
      return null;
    }
  }

  return null;
}

/**
 * Save recommendation cache to memory and localStorage
 *
 * Returns true if cache was saved successfully to at least one storage,
 * false if both storage methods failed.
 */
export function saveCache(products: RecommendationCache["products"], ttl: number): boolean {
  const cache: RecommendationCache = {
    products,
    timestamp: Date.now(),
    ttl,
  };

  let savedToMemory = false;
  let savedToLocalStorage = false;

  // Save to memory
  try {
    memoryCache = cache;
    savedToMemory = true;
  } catch (error) {
    console.warn("Failed to save recommendation cache to memory:", error);
  }

  // Save to localStorage if available
  if (isStorageAvailable()) {
    try {
      localStorage.setItem(STORAGE_KEYS.CACHE, JSON.stringify(cache));
      savedToLocalStorage = true;
    } catch (error) {
      console.warn("Failed to save recommendation cache to localStorage:", error);
    }
  }

  return savedToMemory || savedToLocalStorage;
}

/**
 * Clear recommendation cache from both memory and localStorage
 *
 * Returns true if cache was cleared successfully from at least one storage,
 * false if both storage methods failed.
 */
export function clearCache(): boolean {
  let clearedFromMemory = false;
  let clearedFromLocalStorage = false;

  // Clear memory cache
  try {
    memoryCache = null;
    clearedFromMemory = true;
  } catch (error) {
    console.warn("Failed to clear recommendation cache from memory:", error);
  }

  // Clear localStorage cache if available
  if (isStorageAvailable()) {
    try {
      localStorage.removeItem(STORAGE_KEYS.CACHE);
      clearedFromLocalStorage = true;
    } catch (error) {
      console.warn("Failed to clear recommendation cache from localStorage:", error);
    }
  }

  return clearedFromMemory || clearedFromLocalStorage;
}

/**
 * Get cached products if available and valid
 *
 * Returns the cached products array, or null if no valid cache exists.
 */
export function getCachedProducts(): RecommendationCache["products"] | null {
  const cache = getCache();
  return cache ? cache.products : null;
}
